import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FormattedMessage } from "react-intl";

import { Resource } from "../../../api/useFetch";
import { Offer } from "../../offer";
import CustomDate from "../../../components/Date";
import Price from "../../../components/Price";
import { ServiceClass } from "../../../components/ServiceClass";
import { DistanceOrDuration } from "../DistanceOrDuration";

import styles from "./OfferDialog.module.css";
import { useNewBrandStyle } from "../../../hooks/useNewBrandStyle";
import closeModalIcon from "../../../../static/new-brand/close-icon.svg";

interface OfferDialogProps {
  offer: Resource<Offer> | null;
  pickupAddress: string | undefined;
  dropoffAddress: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  onAccept: (offer: Resource<Offer>) => void;
  isAccepting: boolean;
}

export function OfferDialog({
  offer,
  pickupAddress,
  dropoffAddress,
  isOpen,
  onClose,
  onAccept,
  isAccepting,
}: OfferDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);
  const isNewBrandEnabled = useNewBrandStyle();

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      dialogRef.current?.focus();
      document.body.classList.add("dialog-open");
    }

    return () => {
      document.body.classList.remove("dialog-open");
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAccept = (e: React.FormEvent) => {
    e.preventDefault();
    if (!offer) {
      return;
    }
    onAccept(offer);
  };

  // Dialog only opens on user interaction (client-side only)
  if (!isOpen || !offer) {
    return null;
  }

  // Defensive check for SSR safety
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="offer-dialog-title"
        className={styles.dialog}
        tabIndex={-1}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close dialog"
        >
          {isNewBrandEnabled ? (
            <img src={closeModalIcon} alt="" aria-hidden="true" />
          ) : (
            <CloseIcon />
          )}
        </button>

        <h2 id="offer-dialog-title" className={styles.title}>
          <FormattedMessage id="offer.title" />
        </h2>

        {isNewBrandEnabled ? (
          <>
            <div className={styles.content}>
              <div className={styles.headerDetails}>
                <div className={styles.date}>
                  <CustomDate
                    value={offer.attributes.starts_at}
                    isHighlighted
                  />
                </div>
                {offer.attributes.booking_number && (
                  <div className={styles.bookingNumber}>
                    {offer.attributes.booking_number}
                  </div>
                )}
              </div>
              <div className={styles.divider} />

              {pickupAddress && (
                <div className={styles.property}>
                  <div className={styles.propertyLabel}>
                    <FormattedMessage id="shared.pickup" />
                  </div>
                  <div className={styles.propertyValue}>{pickupAddress}</div>
                </div>
              )}

              {dropoffAddress ? (
                <div className={styles.property}>
                  <div className={styles.propertyLabel}>
                    <FormattedMessage id="shared.dropoff" />
                  </div>
                  <div className={styles.propertyValue}>{dropoffAddress}</div>
                  <DistanceOrDuration ride={offer.attributes} />
                </div>
              ) : (
                <div className={styles.property}>
                  <div className={styles.propertyLabel}>
                    <FormattedMessage id="shared.duration" />
                  </div>
                  <div className={styles.propertyValue}>
                    <DistanceOrDuration ride={offer.attributes} />
                  </div>
                </div>
              )}

              <div className={styles.property}>
                <div className={styles.propertyLabel}>
                  <FormattedMessage id="shared.service-class" />
                </div>
                <div className={styles.propertyValue}>
                  <ServiceClass id={offer.attributes.service_class} />
                </div>
              </div>

              {offer.attributes.flight_number && (
                <div className={styles.property}>
                  <div className={styles.propertyLabel}>
                    <FormattedMessage id="shared.flight-number" />
                  </div>
                  <div className={styles.propertyValue}>
                    {offer.attributes.flight_number}
                  </div>
                </div>
              )}

              {offer.attributes.special_requests && (
                <div className={styles.property}>
                  <div className={styles.propertyLabel}>
                    <FormattedMessage id="shared.special-requests" />
                  </div>
                  <div className={styles.propertyValue}>
                    {offer.attributes.special_requests}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.divider} />
          </>
        ) : (
          <div className={styles.content}>
            {offer.attributes.booking_number && (
              <div className={styles.property}>
                <div className={styles.propertyLabel}>
                  <FormattedMessage id="shared.booking-number" />
                </div>
                <div className={styles.propertyValue}>
                  {offer.attributes.booking_number}
                </div>
              </div>
            )}

            <div className={styles.property}>
              <div className={styles.propertyLabel}>
                <FormattedMessage id="shared.date" />
              </div>
              <div className={styles.propertyValue}>
                <CustomDate value={offer.attributes.starts_at} isHighlighted />
              </div>
            </div>

            {pickupAddress && (
              <div className={styles.property}>
                <div className={styles.propertyLabel}>
                  <FormattedMessage id="shared.pickup" />
                </div>
                <div className={styles.propertyValue}>{pickupAddress}</div>
              </div>
            )}

            {dropoffAddress && (
              <div className={styles.property}>
                <div className={styles.propertyLabel}>
                  <FormattedMessage id="shared.dropoff" />
                </div>
                <div className={styles.propertyValue}>{dropoffAddress}</div>
              </div>
            )}

            <div className={styles.property}>
              <div className={styles.propertyLabel}>
                <FormattedMessage id="shared.service-class" />
              </div>
              <div className={styles.propertyValue}>
                <ServiceClass id={offer.attributes.service_class} />
              </div>
            </div>

            <div className={styles.property}>
              <div className={styles.propertyLabel}>
                <FormattedMessage id="shared.price" />
              </div>
              <div className={styles.propertyValue}>
                <Price
                  currency={offer.attributes.currency}
                  value={parseFloat(offer.attributes.price)}
                />{" "}
                <DistanceOrDuration ride={offer.attributes} />
              </div>
            </div>

            {offer.attributes.flight_number && (
              <div className={styles.property}>
                <div className={styles.propertyLabel}>
                  <FormattedMessage id="shared.flight-number" />
                </div>
                <div className={styles.propertyValue}>
                  {offer.attributes.flight_number}
                </div>
              </div>
            )}

            {offer.attributes.special_requests && (
              <div className={styles.property}>
                <div className={styles.propertyLabel}>
                  <FormattedMessage id="shared.special-requests" />
                </div>
                <div className={styles.propertyValue}>
                  {offer.attributes.special_requests}
                </div>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleAccept}>
          <div className={styles.actions}>
            {isNewBrandEnabled ? (
              <div className={styles.propertyValue}>
                <Price
                  currency={offer.attributes.currency}
                  value={parseFloat(offer.attributes.price)}
                />
              </div>
            ) : (
              <button
                type="button"
                data-type="tertiary"
                onClick={onClose}
                disabled={isAccepting}
              >
                <FormattedMessage id="shared.cancel" />
              </button>
            )}
            <button type="submit" disabled={isAccepting}>
              <FormattedMessage id="offer.accept" />
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function CloseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 6L6 18M6 6L18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
